/**
 * Trainer Assessment Detail
 *
 * View assessment, generate learner link, view submissions.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { RequestPanel, StatusBadge } from '../../components/RequestPanel';
import { Code } from '../../components/JsonViewer';
import { GenerationProgress } from '../../components/GenerationProgress';
import { CompetencyList } from '../../components/CompetencyList';
import { RefinementChat } from '../../components/RefinementChat';
import { AssessmentDocument } from '../../components/AssessmentDocument';
import type {
  ApiResult,
  Assessment,
  ArtifactLink,
  GenerationStatusResponse,
  AssessmentPackageResponse,
  CompetenciesResponse,
  GenerationJobType,
} from '../../lib/types';

export function TrainerAssessmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const justCreated = searchParams.get('created') === 'true';
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [submissions, setSubmissions] = useState<ArtifactLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [result, setResult] = useState<ApiResult<Assessment> | null>(null);
  const [learnerLink, setLearnerLink] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Generation state
  const [generationStatus, setGenerationStatus] = useState<GenerationStatusResponse | null>(null);
  const [triggeringGeneration, setTriggeringGeneration] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Two-step generation state (F-QUIZ-002)
  // Phase: 'none' | 'competencies_pending' | 'competencies_ready' | 'content_pending' | 'complete'
  const [competencies, setCompetencies] = useState<CompetenciesResponse | null>(null);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);
  const [triggeringContent, setTriggeringContent] = useState(false);
  const [currentJobType, setCurrentJobType] = useState<GenerationJobType>('full');

  // Package state (for competency editing)
  const [package_, setPackage] = useState<AssessmentPackageResponse | null>(null);
  const [loadingPackage, setLoadingPackage] = useState(false);
  const [updatingWeights, setUpdatingWeights] = useState(false);
  const [weightsError, setWeightsError] = useState<string | null>(null);

  // Publish state
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  // Clone/versioning state (F-007)
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // WS5: View toggle state (Edit View vs Document View)
  const [viewMode, setViewMode] = useState<'edit' | 'document'>('edit');

  // Voice Agent Prompt state
  const [agentPromptDraft, setAgentPromptDraft] = useState<string>('');
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);

  // Build agent prompt preview from package data
  const generatedAgentPrompt = useMemo(() => {
    if (!package_) return '';
    const { persona, scenario, competencies } = package_;
    const competencyNames = competencies.map((c) => c.name).join(', ');
    const cs = persona.communicationStyle;
    const lines: string[] = [
      `You are ${persona.name}, ${persona.role}.`,
      '',
      '## Background',
      persona.background,
      '',
      '## Scenario',
      `**${scenario.title}**`,
      scenario.context,
      '',
      `**Situation:** ${scenario.situation}`,
      `**The learner's role:** ${scenario.learnerRole}`,
      `**Expected outcome:** ${scenario.expectedOutcome}`,
      '',
      '## Your Communication Style',
      ...(typeof cs === 'object' && cs !== null
        ? [`- Tone: ${cs.tone}`, `- Formality: ${cs.formality}`, `- Pace: ${cs.pace}`, `- Verbosity: ${cs.verbosity}`]
        : [`${cs}`]),
      '',
      '## Your Emotional State',
      persona.emotionalState,
      '',
      '## Your Objectives',
      ...persona.objectives.map((o) => `- ${o}`),
      '',
      '## Constraints',
      ...persona.constraints.map((c) => `- ${c}`),
    ];
    if (scenario.constraints && scenario.constraints.length > 0) {
      lines.push(...scenario.constraints.map((c) => `- ${c}`));
    }
    lines.push(
      '',
      '## Assessment Focus',
      `The learner is being assessed on: ${competencyNames}.`,
      'Guide the conversation naturally to allow the learner to demonstrate these competencies.',
      'Do not explicitly mention that you are assessing them.',
    );
    if (persona.openingStatement) {
      lines.push('', '## Opening', `Begin the conversation with: "${persona.openingStatement}"`);
    }
    if (scenario.suggestedDuration) {
      lines.push('', '## Duration', `This conversation should last approximately ${scenario.suggestedDuration} minutes.`);
    }
    return lines.join('\n');
  }, [package_]);

  // Auto-populate prompt draft when package loads
  useEffect(() => {
    if (generatedAgentPrompt && !agentPromptDraft) {
      setAgentPromptDraft(generatedAgentPrompt);
    }
  }, [generatedAgentPrompt, agentPromptDraft]);

  const loadAssessment = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await apiClient.getAssessment(id);
    setResult(res);
    if (res.response.data) {
      setAssessment(res.response.data);
    }
    setLoading(false);
  }, [id]);

  const loadGenerationStatus = useCallback(async () => {
    if (!id) return;
    const res = await apiClient.getGenerationStatus(id);
    if (res.response.data) {
      setGenerationStatus(res.response.data);
      // Sync currentJobType from status response if present
      if (res.response.data.jobType) {
        setCurrentJobType(res.response.data.jobType);
      }
      // Stop polling if job is completed or failed
      const status = res.response.data.status;
      if (status === 'completed' || status === 'failed' || status === null) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    }
  }, [id]);

  const loadPackage = useCallback(async () => {
    if (!id) return;
    setLoadingPackage(true);
    try {
      const res = await apiClient.getAssessmentPackage(id);
      if (res.response.data) {
        setPackage(res.response.data);
      }
    } finally {
      setLoadingPackage(false);
    }
  }, [id]);

  // Load competencies from completed competencies-only job (F-QUIZ-002)
  const loadCompetencies = useCallback(async () => {
    if (!id) return;
    setLoadingCompetencies(true);
    try {
      const res = await apiClient.getCompetencies(id);
      if (res.response.data) {
        setCompetencies(res.response.data);
      }
    } catch {
      // Competencies not available yet - this is OK
      setCompetencies(null);
    } finally {
      setLoadingCompetencies(false);
    }
  }, [id]);

  const handleWeightsUpdate = useCallback(
    async (weights: Record<string, number>, version: number) => {
      if (!id) return;
      setUpdatingWeights(true);
      setWeightsError(null);

      try {
        const res = await apiClient.updateCompetencyWeights(id, { weights, version });

        if (res.response.error || !res.response.data) {
          throw new Error(res.response.error?.message ?? 'Failed to update weights');
        }

        // Update local package state with response
        setPackage(res.response.data);
      } catch (err) {
        console.error('Failed to update weights:', err);
        setWeightsError(err instanceof Error ? err.message : 'Failed to update weights');
        throw err; // Re-throw so CompetencyList knows update failed
      } finally {
        setUpdatingWeights(false);
      }
    },
    [id]
  );

  const handlePublish = useCallback(async () => {
    if (!id || !assessment) return;
    setPublishing(true);
    setPublishError(null);

    try {
      const publishData: { version: number; agentPrompt?: string } = { version: assessment.version };
      // If trainer customized the prompt, send it; otherwise backend builds from package
      if (agentPromptDraft && agentPromptDraft !== generatedAgentPrompt) {
        publishData.agentPrompt = agentPromptDraft;
      }
      const res = await apiClient.publishAssessment(id, publishData);

      if (res.response.error || !res.response.data) {
        throw new Error(res.response.error?.message ?? 'Failed to publish assessment');
      }

      // Update local assessment state
      setAssessment(res.response.data);
      setShowPublishConfirm(false);
    } catch (err) {
      console.error('Failed to publish assessment:', err);
      setPublishError(err instanceof Error ? err.message : 'Failed to publish assessment');
    } finally {
      setPublishing(false);
    }
  }, [id, assessment, agentPromptDraft, generatedAgentPrompt]);

  const handleClone = useCallback(async () => {
    if (!id) return;
    setCloning(true);
    setCloneError(null);

    try {
      const res = await apiClient.cloneAssessment(id, { clonePackage: true });

      if (res.response.error || !res.response.data) {
        throw new Error(res.response.error?.message ?? 'Failed to create new version');
      }

      // Navigate to the new cloned assessment
      navigate(`/console/trainer/assessments/${res.response.data.id}?created=true`);
    } catch (err) {
      console.error('Failed to clone assessment:', err);
      setCloneError(err instanceof Error ? err.message : 'Failed to create new version');
    } finally {
      setCloning(false);
    }
  }, [id, navigate]);

  const loadSubmissions = useCallback(async () => {
    if (!id) return;
    setLoadingSubmissions(true);

    // Get artifact links for this assessment - only 'source' linkType is valid
    const res = await apiClient.listArtifactLinks({ assessmentId: id, limit: 100 });

    if (res.response.data) {
      // Filter for source links only (audio submissions linked to assessment)
      const sourceLinks = res.response.data.items.filter(
        (link) => link.linkType === 'source'
      );
      setSubmissions(sourceLinks);
    }

    setLoadingSubmissions(false);
  }, [id]);

  useEffect(() => {
    loadAssessment();
    loadSubmissions();
    loadGenerationStatus();
  }, [loadAssessment, loadSubmissions, loadGenerationStatus]);

  // Load full package when generation completes or if package exists
  useEffect(() => {
    if (generationStatus?.hasPackage && !package_ && !loadingPackage) {
      loadPackage();
    }
  }, [generationStatus?.hasPackage, package_, loadingPackage, loadPackage]);

  // Load competencies when competencies-only job completes (F-QUIZ-002)
  useEffect(() => {
    // If competencies job completed, load competencies for review
    // Use jobType to determine if this was a competencies-only job
    if (
      generationStatus?.status === 'completed' &&
      generationStatus?.jobType === 'competencies' &&
      !competencies &&
      !loadingCompetencies
    ) {
      loadCompetencies();
    }
  }, [generationStatus?.status, generationStatus?.jobType, competencies, loadingCompetencies, loadCompetencies]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Start polling if there's an active generation job on page load
  useEffect(() => {
    if (
      generationStatus?.status &&
      generationStatus.status !== 'completed' &&
      generationStatus.status !== 'failed' &&
      !pollingRef.current
    ) {
      pollingRef.current = setInterval(loadGenerationStatus, 2000);
    }
  }, [generationStatus?.status, loadGenerationStatus]);

  const [generatingLink, setGeneratingLink] = useState(false);
  const [showCreatedBanner, setShowCreatedBanner] = useState(justCreated);

  // Clear the 'created' query param after showing banner
  useEffect(() => {
    if (justCreated) {
      // Remove the query param from URL without navigation
      setSearchParams({}, { replace: true });
      // Auto-hide banner after 5 seconds
      const timer = setTimeout(() => setShowCreatedBanner(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [justCreated, setSearchParams]);

  // Step 1: Trigger competency generation only (F-QUIZ-002)
  const triggerCompetencyGeneration = async () => {
    if (!id) return;
    setTriggeringGeneration(true);
    setGenerationError(null);
    setCurrentJobType('competencies');

    // Clear stale data when starting regeneration
    setCompetencies(null);
    setPackage(null);

    try {
      const additionalContext = assessment?.metadata?.additionalContext as string | undefined;
      const res = await apiClient.triggerCompetencyGeneration(id, {
        additionalContext: additionalContext || undefined,
      });

      if (res.response.error || !res.response.data) {
        throw new Error(res.response.error?.message ?? 'Failed to trigger competency generation');
      }

      // Start polling for status updates
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(loadGenerationStatus, 2000);

      await loadGenerationStatus();
    } catch (err) {
      console.error('Failed to trigger competency generation:', err);
      setGenerationError(err instanceof Error ? err.message : 'Failed to trigger competency generation');
    } finally {
      setTriggeringGeneration(false);
    }
  };

  // Step 2: Trigger content generation from approved competencies (F-QUIZ-002)
  const triggerContentGeneration = async () => {
    if (!id) return;
    setTriggeringContent(true);
    setGenerationError(null);
    setCurrentJobType('content');

    try {
      const res = await apiClient.triggerContentGeneration(id);

      if (res.response.error || !res.response.data) {
        throw new Error(res.response.error?.message ?? 'Failed to trigger content generation');
      }

      // Start polling for status updates
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(loadGenerationStatus, 2000);

      await loadGenerationStatus();
    } catch (err) {
      console.error('Failed to trigger content generation:', err);
      setGenerationError(err instanceof Error ? err.message : 'Failed to trigger content generation');
    } finally {
      setTriggeringContent(false);
    }
  };

  // Legacy: Trigger full generation (backwards compatible, but not used in new UI flow)
  const triggerGeneration = async () => {
    if (!id) return;
    setTriggeringGeneration(true);
    setGenerationError(null);
    setCurrentJobType('full');

    try {
      // Extract additionalContext from assessment metadata if present
      const additionalContext = assessment?.metadata?.additionalContext as string | undefined;
      const res = await apiClient.triggerGeneration(id, {
        additionalContext: additionalContext || undefined,
      });

      if (res.response.error || !res.response.data) {
        throw new Error(res.response.error?.message ?? 'Failed to trigger generation');
      }

      // Start polling for status updates (every 2 seconds)
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(loadGenerationStatus, 2000);

      // Immediately load status
      await loadGenerationStatus();
    } catch (err) {
      console.error('Failed to trigger generation:', err);
      setGenerationError(err instanceof Error ? err.message : 'Failed to trigger generation');
    } finally {
      setTriggeringGeneration(false);
    }
  };

  const generateLearnerLink = async () => {
    if (!id) return;
    setGeneratingLink(true);
    setLinkError(null);

    try {
      // Call backend to generate scoped learner token
      const result = await apiClient.createLearnerToken(id);

      if (result.response.error || !result.response.data) {
        const errorMsg = result.response.error?.message ?? 'Failed to generate token';
        throw new Error(errorMsg);
      }

      const { token } = result.response.data;

      // Build learner link with token as query param
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/learner/test/${id}?token=${encodeURIComponent(token)}`;
      setLearnerLink(link);
      setCopied(false);
    } catch (err) {
      console.error('Failed to generate learner link:', err);
      setLinkError(err instanceof Error ? err.message : 'Failed to generate link. Please try again.');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = async () => {
    if (!learnerLink) return;
    try {
      await navigator.clipboard.writeText(learnerLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = learnerLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // WS5: Preview as Learner - opens learner URL in new tab using short-lived preview token
  const handlePreviewAsLearner = async () => {
    if (!id) return;
    try {
      const result = await apiClient.createPreviewToken(id);
      if (result.response.error || !result.response.data) {
        console.error('Failed to create preview token:', result.response.error?.message);
        return;
      }
      const { token } = result.response.data;
      const baseUrl = window.location.origin;
      const previewUrl = `${baseUrl}/learner/test/${id}?token=${encodeURIComponent(token)}`;
      window.open(previewUrl, '_blank');
    } catch (err) {
      console.error('Failed to preview as learner:', err);
    }
  };

  // WS5: PDF export via @media print + window.print()
  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Link to="/console/trainer/assessments" style={{ color: 'var(--text-tertiary)' }}>
          &larr; Back to Assessments
        </Link>
      </div>

      {/* WS5: View toggle - Edit View vs Document View */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0 }}>Assessment Detail</h1>
        {assessment && package_ && (
          <div
            style={{
              display: 'flex',
              background: 'var(--slate-100)',
              borderRadius: 'var(--radius-md)',
              padding: 2,
            }}
          >
            <button
              onClick={() => setViewMode('edit')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                background: viewMode === 'edit' ? 'white' : 'transparent',
                color: viewMode === 'edit' ? 'var(--text-primary)' : 'var(--text-secondary)',
                boxShadow: viewMode === 'edit' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              Edit View
            </button>
            <button
              onClick={() => setViewMode('document')}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                border: 'none',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                background: viewMode === 'document' ? 'white' : 'transparent',
                color: viewMode === 'document' ? 'var(--text-primary)' : 'var(--text-secondary)',
                boxShadow: viewMode === 'document' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              Document View
            </button>
          </div>
        )}
      </div>

      {showCreatedBanner && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--semantic-success-light)',
            borderRadius: 4,
            border: '1px solid var(--semantic-success-border)',
            color: 'var(--semantic-success-text)',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            <strong>Assessment created successfully.</strong> Generate a learner link to begin testing.
          </span>
          <button
            onClick={() => setShowCreatedBanner(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--semantic-success-text)',
              cursor: 'pointer',
              fontSize: 18,
              padding: '0 4px',
            }}
          >
            &times;
          </button>
        </div>
      )}

      {/* WS5: Document View - alternate render path */}
      {assessment && package_ && viewMode === 'document' && (
        <AssessmentDocument
          assessment={assessment}
          package_={package_}
          onPrint={handlePrint}
          onPreviewAsLearner={handlePreviewAsLearner}
        />
      )}

      {assessment && viewMode === 'edit' && (
        <>
          {/* Summary */}
          <div className="card">
            <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Summary</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                {assessment.status === 'draft' && (
                  <Link to={`/console/trainer/assessments/${id}/edit`} className="btn btn-secondary">
                    Edit
                  </Link>
                )}
                {(assessment.status === 'pending' || assessment.status === 'complete') && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleClone}
                    disabled={cloning}
                  >
                    {cloning ? 'Creating...' : 'Create New Version'}
                  </button>
                )}
              </div>
            </div>
            {cloneError && (
              <div className="error-box" style={{ marginBottom: 16 }}>
                {cloneError}
              </div>
            )}
            <table className="table" style={{ maxWidth: 600 }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500, width: 180 }}>Name</td>
                  <td>{(assessment.metadata?.name as string) || <span className="text-muted">-</span>}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Objective</td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>
                    {(assessment.metadata?.objective as string) || <span className="text-muted">-</span>}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Assessment Timing</td>
                  <td>
                    {assessment.assessmentType === 'pre' && 'Pre-training'}
                    {assessment.assessmentType === 'post' && 'Post-training'}
                    {!assessment.assessmentType && <span className="text-muted">-</span>}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>ID</td>
                  <td>
                    <Code>{assessment.id}</Code>
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Status</td>
                  <td>
                    <StatusBadge status={assessment.status} />
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Rubric</td>
                  <td>
                    {assessment.rubricVersion ? (
                      <Link to={`/console/trainer/rubrics/${assessment.rubricVersion}`}>
                        <Code>{assessment.rubricVersion}</Code>
                      </Link>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                </tr>
                {assessment.externalReferenceId && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>External Reference</td>
                    <td>{assessment.externalReferenceId}</td>
                  </tr>
                )}
                {assessment.sourceSystem && (
                  <tr>
                    <td style={{ fontWeight: 500 }}>Source System</td>
                    <td>{assessment.sourceSystem}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ fontWeight: 500 }}>Created At</td>
                  <td>{new Date(assessment.createdAt).toLocaleString()}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>Created By</td>
                  <td>
                    <Code>{assessment.createdBy}</Code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* AI Package Generation Section - only for draft assessments */}
          {assessment.status === 'draft' && (
            <div className="card">
              <h2>AI Package Generation</h2>
              <p className="text-muted" style={{ marginBottom: 16 }}>
                Two-step generation: Generate competencies first, review, then generate content.
              </p>

              {generationError && (
                <div className="error-box" style={{ marginBottom: 16 }}>
                  {generationError}
                </div>
              )}

              {/* Phase 1: No generation started yet - show "Generate Competencies" button */}
              {!generationStatus?.jobId && !triggeringGeneration && !competencies && !package_ && (
                <div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600 }}>
                    Step 1: Generate Competencies
                  </h4>
                  <p className="text-muted" style={{ marginBottom: 12, fontSize: 13 }}>
                    AI will analyze the assessment objective and generate competencies for review.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={triggerCompetencyGeneration}
                    disabled={triggeringGeneration}
                  >
                    Generate Competencies
                  </button>
                </div>
              )}

              {/* Generation in progress */}
              {(generationStatus?.jobId || triggeringGeneration || triggeringContent) &&
                generationStatus?.status !== 'completed' &&
                generationStatus?.status !== 'failed' && (
                  <GenerationProgress
                    status={
                      triggeringGeneration || triggeringContent
                        ? 'pending'
                        : generationStatus?.status ?? null
                    }
                    progressMessage={generationStatus?.progressMessage ?? null}
                    errorMessage={generationStatus?.errorMessage ?? null}
                    packageSummary={generationStatus?.package ?? null}
                    startedAt={generationStatus?.startedAt ?? null}
                    completedAt={generationStatus?.completedAt ?? null}
                    jobType={currentJobType}
                  />
                )}

              {/* Phase 2: Competencies generated, no package yet - show competency review */}
              {competencies && !package_ && (
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      padding: '12px 16px',
                      background: 'var(--semantic-success-light)',
                      borderRadius: 4,
                      marginBottom: 16,
                    }}
                  >
                    <strong style={{ color: 'var(--semantic-success-text)' }}>
                      Step 1 Complete: Competencies Generated
                    </strong>
                    <p style={{ margin: '8px 0 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                      Review the {competencies.competencies.length} competencies below. You can adjust
                      weights before proceeding to content generation.
                    </p>
                  </div>

                  {/* Show competencies for review - reuse CompetencyList but convert format */}
                  {competencies.competencies.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600 }}>
                        Generated Competencies ({competencies.competencies.length})
                      </h4>
                      <table className="table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ padding: '8px 12px' }}>Name</th>
                            <th style={{ padding: '8px 12px' }}>Category</th>
                            <th style={{ padding: '8px 12px' }}>Type</th>
                            <th style={{ padding: '8px 12px', textAlign: 'right' }}>Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {competencies.competencies.map((c) => (
                            <tr key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ fontWeight: 500 }}>{c.name}</div>
                                <div
                                  style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}
                                >
                                  {c.description}
                                </div>
                              </td>
                              <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                                {c.category || '-'}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <span
                                  style={{
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    background:
                                      c.scoringType === 'rubric'
                                        ? 'var(--semantic-info-light)'
                                        : c.scoringType === 'scale'
                                          ? 'var(--semantic-warning-light)'
                                          : 'var(--bg-muted)',
                                    color:
                                      c.scoringType === 'rubric'
                                        ? 'var(--semantic-info-text)'
                                        : c.scoringType === 'scale'
                                          ? 'var(--semantic-warning-text)'
                                          : 'var(--text-secondary)',
                                  }}
                                >
                                  {c.scoringType}
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                <span className="mono">{(c.weight * 100).toFixed(1)}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Step 2 button */}
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: '1px solid var(--border-default)',
                    }}
                  >
                    <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600 }}>
                      Step 2: Generate Content
                    </h4>
                    <p className="text-muted" style={{ marginBottom: 12, fontSize: 13 }}>
                      Generate scenario, persona, and rubric based on these competencies.
                    </p>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button
                        className="btn btn-primary"
                        onClick={triggerContentGeneration}
                        disabled={triggeringContent}
                      >
                        {triggeringContent ? 'Generating...' : 'Approve & Generate Content'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={triggerCompetencyGeneration}
                        disabled={triggeringGeneration}
                      >
                        Regenerate Competencies
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Failed generation - show regenerate option */}
              {generationStatus?.status === 'failed' && (
                <div style={{ marginTop: 16 }}>
                  <GenerationProgress
                    status="failed"
                    progressMessage={null}
                    errorMessage={generationStatus?.errorMessage ?? null}
                    packageSummary={null}
                    startedAt={generationStatus?.startedAt ?? null}
                    completedAt={generationStatus?.completedAt ?? null}
                    jobType={currentJobType}
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={package_ ? triggerGeneration : triggerCompetencyGeneration}
                    disabled={triggeringGeneration}
                    style={{ marginTop: 16 }}
                  >
                    {triggeringGeneration ? 'Starting...' : 'Try Again'}
                  </button>
                </div>
              )}

              {/* Phase 3: Full package exists */}
              {package_ && (
                <>
                  {/* Show package completion */}
                  {generationStatus?.status === 'completed' && generationStatus?.hasPackage && (
                    <GenerationProgress
                      status="completed"
                      progressMessage={null}
                      errorMessage={null}
                      packageSummary={generationStatus?.package ?? null}
                      startedAt={generationStatus?.startedAt ?? null}
                      completedAt={generationStatus?.completedAt ?? null}
                      jobType={currentJobType}
                    />
                  )}

                  {/* Competency List with weight editing */}
                  <CompetencyList
                    package_={package_}
                    isDraft={assessment.status === 'draft'}
                    onWeightsUpdate={handleWeightsUpdate}
                    isUpdating={updatingWeights}
                    updateError={weightsError}
                  />

                  {/* --- Refinement Chat Section --- */}
                  {id && (
                    <RefinementChat
                      assessmentId={id}
                      package_={package_}
                      onPackageUpdated={(updatedPkg) => {
                        setPackage(updatedPkg);
                      }}
                    />
                  )}

                  {/* Regenerate option */}
                  <button
                    className="btn btn-secondary"
                    onClick={triggerCompetencyGeneration}
                    disabled={triggeringGeneration}
                    style={{ marginTop: 16 }}
                  >
                    {triggeringGeneration ? 'Starting...' : 'Regenerate (Start Over)'}
                  </button>
                </>
              )}

              {loadingPackage && (
                <p className="text-muted" style={{ marginTop: 16 }}>
                  Loading package details...
                </p>
              )}

              {loadingCompetencies && (
                <p className="text-muted" style={{ marginTop: 16 }}>
                  Loading competencies...
                </p>
              )}

              {/* Voice Agent Prompt - only for voice mode with package */}
              {package_ && assessment.assessmentMode !== 'quiz' && generatedAgentPrompt && (
                <div
                  style={{
                    marginTop: 24,
                    paddingTop: 16,
                    borderTop: '1px solid var(--border-default)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                      onClick={() => setPromptExpanded(!promptExpanded)}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          transform: promptExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s ease',
                          fontSize: 12,
                        }}
                      >
                        {'\u25B6'}
                      </span>
                      Voice Agent System Prompt
                    </h4>
                    {promptExpanded && assessment.status === 'draft' && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: 11, padding: '2px 10px' }}
                        onClick={() => {
                          if (editingPrompt) {
                            setEditingPrompt(false);
                          } else {
                            setEditingPrompt(true);
                          }
                        }}
                      >
                        {editingPrompt ? 'Done Editing' : 'Edit'}
                      </button>
                    )}
                  </div>
                  <p className="text-muted" style={{ marginBottom: 8, fontSize: 12 }}>
                    This is the system prompt the AI voice agent will use when talking to learners.
                  </p>
                  {promptExpanded && (
                    <>
                      {editingPrompt ? (
                        <div>
                          <textarea
                            value={agentPromptDraft}
                            onChange={(e) => setAgentPromptDraft(e.target.value)}
                            style={{
                              width: '100%',
                              minHeight: 300,
                              fontFamily: 'monospace',
                              fontSize: 12,
                              lineHeight: 1.5,
                              padding: 12,
                              borderRadius: 6,
                              border: '1px solid var(--sky-300)',
                              background: 'var(--bg-surface)',
                              resize: 'vertical',
                              outline: 'none',
                            }}
                          />
                          {agentPromptDraft !== generatedAgentPrompt && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                              <span style={{ fontSize: 12, color: 'var(--sky-600)' }}>
                                Custom edits will be used when publishing.
                              </span>
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: 11, padding: '2px 10px' }}
                                onClick={() => setAgentPromptDraft(generatedAgentPrompt)}
                              >
                                Reset to Generated
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <pre
                          style={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontSize: 12,
                            lineHeight: 1.5,
                            background: 'var(--bg-muted)',
                            padding: 12,
                            borderRadius: 6,
                            maxHeight: 400,
                            overflow: 'auto',
                            margin: 0,
                          }}
                        >
                          {agentPromptDraft || generatedAgentPrompt}
                        </pre>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Publish Section - only when draft and has package */}
              {assessment.status === 'draft' && package_ && (
                <div
                  style={{
                    marginTop: 24,
                    paddingTop: 16,
                    borderTop: '1px solid var(--border-default)',
                  }}
                >
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600 }}>
                    Publish Assessment
                  </h4>
                  <p className="text-muted" style={{ marginBottom: 12, fontSize: 13 }}>
                    Publishing makes the assessment available to learners. Competency weights will be
                    locked and cannot be changed after publishing.
                  </p>

                  {publishError && (
                    <div className="error-box" style={{ marginBottom: 12 }}>
                      {publishError}
                    </div>
                  )}

                  <button
                    className="btn btn-primary"
                    onClick={() => setShowPublishConfirm(true)}
                    disabled={publishing}
                  >
                    {publishing ? 'Publishing...' : 'Publish Assessment'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Package details for non-draft assessments that have a package */}
          {assessment.status !== 'draft' && generationStatus?.hasPackage && (
            <div className="card">
              <h2>Assessment Package</h2>

              {/* Summary info */}
              {generationStatus.package && (
                <div
                  style={{
                    padding: '12px 16px',
                    background: 'var(--bg-muted)',
                    borderRadius: 4,
                    fontSize: 13,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                    <div>
                      <span style={{ color: 'var(--text-secondary)' }}>Scenario:</span>{' '}
                      <strong>{generationStatus.package.scenarioTitle}</strong>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-secondary)' }}>Persona:</span>{' '}
                      <strong>{generationStatus.package.personaName}</strong>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-secondary)' }}>Generated:</span>{' '}
                      <span className="mono" style={{ fontSize: 11 }}>
                        {new Date(generationStatus.package.generatedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Competency List - read-only for published assessments */}
              {package_ && (
                <CompetencyList
                  package_={package_}
                  isDraft={false}
                  onWeightsUpdate={handleWeightsUpdate}
                  isUpdating={false}
                  updateError={null}
                />
              )}

              {loadingPackage && (
                <p className="text-muted">Loading competency details...</p>
              )}

              {/* Agent prompt (read-only) for published voice assessments */}
              {typeof assessment.metadata?.agentPrompt === 'string' && (
                <div style={{ marginTop: 16 }}>
                  <h4
                    style={{
                      margin: '0 0 8px 0',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    onClick={() => setPromptExpanded(!promptExpanded)}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        transform: promptExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s ease',
                        fontSize: 12,
                      }}
                    >
                      {'\u25B6'}
                    </span>
                    Voice Agent System Prompt
                  </h4>
                  {promptExpanded && (
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 12,
                        lineHeight: 1.5,
                        background: 'var(--bg-muted)',
                        padding: 12,
                        borderRadius: 6,
                        maxHeight: 400,
                        overflow: 'auto',
                        margin: 0,
                      }}
                    >
                      {String(assessment.metadata.agentPrompt)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Learner Link Section */}
          <div className="card">
            <h2>Learner Test Link</h2>
            <p className="text-muted" style={{ marginBottom: 16 }}>
              Generate a shareable link for learners to take this assessment.
            </p>

            {linkError && (
              <div className="error-box" style={{ marginBottom: 16 }}>
                {linkError}
              </div>
            )}

            {assessment.status === 'draft' ? (
              <div
                style={{
                  padding: '16px 20px',
                  background: 'var(--amber-50)',
                  borderRadius: 8,
                  border: '1px solid var(--amber-100)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 20, lineHeight: 1 }}>{'\u{1F512}'}</span>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--amber-700)', marginBottom: 4 }}>
                    Publish required
                  </div>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--amber-600)' }}>
                    You need to publish this assessment before generating a learner link.
                    Publishing locks the content and makes it available for learners.
                  </p>
                </div>
              </div>
            ) : !learnerLink ? (
              <button
                className="btn btn-primary"
                onClick={generateLearnerLink}
                disabled={generatingLink}
              >
                {generatingLink ? 'Generating...' : 'Generate Learner Link'}
              </button>
            ) : (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <input
                    type="text"
                    className="input mono"
                    value={learnerLink}
                    readOnly
                    style={{ flex: 1, maxWidth: 500 }}
                  />
                  <button className="btn btn-secondary" onClick={copyToClipboard}>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                {/* Token expiry warning - "7 days" matches DEFAULT_LEARNER_TOKEN_TTL_SECONDS in backend.
                    Actual expiry is captured at mint time in JWT exp claim (see create-learner-token.ts). */}
                <div
                  style={{
                    padding: '12px 16px',
                    background: 'var(--semantic-warning-light)',
                    borderRadius: 4,
                    border: '1px solid var(--semantic-warning-border)',
                    color: 'var(--semantic-warning-text)',
                    fontSize: 13,
                  }}
                >
                  <strong>Time-limited link.</strong> Token expires in 7 days. Generate a new link if needed.
                </div>
              </div>
            )}
          </div>

          {/* Submissions Section */}
          <div className="card">
            <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
              <h2>Submissions</h2>
              <button
                className="btn btn-secondary"
                onClick={loadSubmissions}
                disabled={loadingSubmissions}
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                Refresh
              </button>
            </div>

            {loadingSubmissions ? (
              <p className="text-muted">Loading submissions...</p>
            ) : submissions.length === 0 ? (
              <p className="text-muted">No submissions yet.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Artifact ID</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((link) => (
                    <tr key={link.id}>
                      <td>
                        <Code>{link.artifactId.slice(0, 12)}...</Code>
                      </td>
                      <td className="text-muted text-small">
                        {new Date(link.createdAt).toLocaleString()}
                      </td>
                      <td>
                        <Link
                          to={`/console/trainer/submissions/${link.artifactId}?assessmentId=${id}`}
                          className="btn btn-secondary"
                          style={{ padding: '4px 8px', fontSize: 12 }}
                        >
                          View Results
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

                      </div>
        </>
      )}

      {/* Publish Confirmation Dialog */}
      {showPublishConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowPublishConfirm(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: 450,
              margin: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px 0' }}>Publish Assessment?</h3>
            <p style={{ marginBottom: 16, color: 'var(--text-secondary)' }}>
              This will make the assessment available to learners. Once published:
            </p>
            <ul
              style={{
                margin: '0 0 16px 0',
                paddingLeft: 20,
                color: 'var(--text-secondary)',
                fontSize: 13,
              }}
            >
              <li>Competency weights will be locked</li>
              <li>The scenario and persona cannot be regenerated</li>
              <li>Learners can begin taking the assessment</li>
            </ul>

            {publishError && (
              <div className="error-box" style={{ marginBottom: 16 }}>
                {publishError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowPublishConfirm(false)}
                disabled={publishing}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handlePublish} disabled={publishing}>
                {publishing ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RequestPanel result={result} loading={loading} />
    </div>
  );
}
